import { useCallback, useEffect, useRef, useState } from 'react';
import { batch } from 'react-redux';
import {
  DateKindKey,
  ExpressionModeKey,
  FunnelModeKey,
  QueryType,
  TimeUnit,
} from '../../../utils/apollo/graphqlSchema';
import { ITooltipProps } from '../hooks/tooltip/useSingleChartDataTooltip';
import { useOnClickOutside } from '../../../utils/hooks';
import { useBootstrapSelector } from '../../../redux/selectorHooks';
import { assertCondition, assertNotNil } from '../../../utils/types';
import {
  ExpressionPostfixOnColumnName,
  ILineMetaNewExpression,
  ILineMetaType,
  IParsedChartDataRecord,
} from '../interfaces';
import TooltipLineLabel from './TooltipLineLabel';
import checkCompoundCohortByHint from '../../../utils/createCohort/checkCompoundCohortByHint';
import { ITooltipContext } from '../CustomTooltip/tooltipContextInterfaces';
import * as SingleChartDataTooltipUI from './SingleChartDataTooltipUI';
import TooltipHeader from './TooltipHeader';
import TooltipLineValue from './TooltipLineValue';
import { useAppDispatch } from '../../../redux/hooks';
import {
  toggleAllTypeSwitchesAction,
  toggleIncidentCreateModalAction,
} from '../../../redux/slices/analyses';
import { OtherLineName } from '../hooks/useOthersLine';
import TooltipExpressionDataChangeValue from './TooltipExpressionDataChangeValue';
import { SUBJECT_EVENT_KEY } from '../constants/utils';
import {
  isScratchpadAnalysisId,
  isTransientAnalysisId,
} from '../../../redux/utils/transientAnalysis';
import { isMappedComparisonKey } from '../../../utils/redux/transformExpressionData';
import { useFloatingLayerContext } from '../../FloatingManager';
import SingleChartDataTooltipMenuItem from './SingleChartDataTooltipMenuItem';
import { createPortal } from 'react-dom';
import { useChartContext } from '../ChartContext';

// hard code the offset to 40px.
// use it to help calculate the position of the tooltip when flipping.
const TOOLTIP_HORIZONTAL_FLIP_OFFSET = 40;
interface IProps {
  analysisId: string;
  handleCreateCohort: (
    tooltipProps: ITooltipProps,
    lineMeta: ILineMetaNewExpression[string],
    isExecuteAfterRedirect: boolean,
  ) => void;
  tooltipProps: ITooltipProps;
  clearLockedTooltipProps: () => void;
  isTooltipLocked: boolean;
  funnelTooltipContext?: ITooltipContext;
  chartDataWithOthers?: IParsedChartDataRecord[];
  linesMetaWithOthers?: ILineMetaNewExpression;
}

const SingleChartDataTooltip = ({
  analysisId,
  handleCreateCohort,
  tooltipProps,
  clearLockedTooltipProps,
  isTooltipLocked,
  funnelTooltipContext,
  chartDataWithOthers,
  linesMetaWithOthers,
}: IProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const dispatch = useAppDispatch();
  const {
    displayedSlices,
    displayedMeasures,
    chartData,
    settings: { chartSettings },
    metadata: { query, comparisonInfo, timeUnit, isEmbedded },
  } = useChartContext();
  assertCondition(
    chartData.queryType === QueryType.Expression ||
      chartData.queryType === QueryType.Funnel,
  );
  const { linesMeta } = chartData;
  const { singleDayLabel, isFunnelRelativeLabels, activeIncidentTypes } =
    chartSettings;

  const isScratchpadAnalysis = isScratchpadAnalysisId(analysisId);

  // Only analysis with "Event" date is supported in cohort
  // and there is no create cohort from impact analysis
  const notAllowCreateCohortFromExpression =
    query?.__typename === 'ExpressionQuery' &&
    (query.dateKind.key !== DateKindKey.Event ||
      query.expressionMode.key === ExpressionModeKey.Impact);
  const notAllowCreateCohortFromFunnel =
    query?.__typename === 'FunnelQuery' &&
    (query.mode?.key === FunnelModeKey.Frequency ||
      // Only analysis with "Event" date is supported in cohort
      query.dateKind?.key !== DateKindKey.Event ||
      // Only analysis with "Event" subject are not supported
      query.subject?.key === SUBJECT_EVENT_KEY ||
      // transient analyses can't be used to create cohort
      isTransientAnalysisId(analysisId));

  const builderCohorts =
    'breakdown' in query && query.breakdown?.cohortRefs
      ? query.breakdown.cohortRefs
      : [];
  const containerRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(containerRef, () => clearLockedTooltipProps());
  const defaultConfig = useBootstrapSelector('defaultConfig');
  assertNotNil(defaultConfig);

  const handleCreateIncident = useCallback(() => {
    batch(() => {
      if (!activeIncidentTypes.showAll) {
        // KB-5974 - display all incidents in the chart
        dispatch(toggleAllTypeSwitchesAction(analysisId));
      }
      dispatch(
        toggleIncidentCreateModalAction({
          analysisId,
          modalState: true,
          modalData: {
            date: tooltipProps.date,
          },
        }),
      );
    });

    clearLockedTooltipProps();
  }, [
    activeIncidentTypes,
    analysisId,
    dispatch,
    clearLockedTooltipProps,
    tooltipProps,
  ]);

  // for others line, we don't have linesMeta
  const lineMeta = linesMeta[tooltipProps.line.name] ?? null;

  const measureFormat =
    displayedMeasures.find(
      (measure) => measure.key === tooltipProps.line.measureKey,
    )?.format ?? null;

  // if compound cohort, not allowed to create cohort or inspect users
  const isCompoundCohort = checkCompoundCohortByHint(
    displayedSlices.find((slice) => lineMeta && slice.key === lineMeta.sliceKey)
      ?.originalHint,
    builderCohorts ?? [],
  );
  const hideMenuForNonBaseLine =
    chartData.queryType === QueryType.Expression &&
    lineMeta?.type !== ILineMetaType.Base;
  const hideMenuItems =
    isMappedComparisonKey(tooltipProps.line.name) ||
    hideMenuForNonBaseLine ||
    notAllowCreateCohortFromExpression ||
    notAllowCreateCohortFromFunnel ||
    isCompoundCohort ||
    [
      OtherLineName.key,
      OtherLineName.key + ExpressionPostfixOnColumnName.percentKey,
    ].includes(tooltipProps.line.name) ||
    isScratchpadAnalysis ||
    isEmbedded ||
    // hide the menu if the schema is disabled
    query.schema.disabled;

  const showCreateIncident = timeUnit !== TimeUnit.AllTime && !singleDayLabel;
  const handleCreateCohortClick = useCallback(() => {
    handleCreateCohort(tooltipProps, lineMeta, false);
    clearLockedTooltipProps();
  }, [handleCreateCohort, lineMeta, clearLockedTooltipProps, tooltipProps]);
  const handleInspectUsersClick = useCallback(() => {
    handleCreateCohort(tooltipProps, lineMeta, true);
    clearLockedTooltipProps();
  }, [handleCreateCohort, lineMeta, clearLockedTooltipProps, tooltipProps]);

  const zIndex = useFloatingLayerContext();

  // knowing the current position of the tooltip, and the rect of the container,
  // need to calculate the position of the tooltip if it's going to overflow on right side or bottom side
  // and return the styles for the tooltip
  const updatePosition = useCallback(() => {
    if (containerRef.current) {
      const tooltipRect = containerRef.current.getBoundingClientRect();
      const wouldOverflowRight =
        tooltipProps.x + tooltipRect.width > window.innerWidth;
      const wouldOverflowBottom =
        tooltipProps.y + tooltipRect.height > window.innerHeight;

      const position = {
        top: wouldOverflowBottom
          ? tooltipProps.y - tooltipRect.height
          : tooltipProps.y,
        left: wouldOverflowRight
          ? tooltipProps.x -
            tooltipRect.width -
            (tooltipProps.barWidth ?? 0) -
            TOOLTIP_HORIZONTAL_FLIP_OFFSET
          : tooltipProps.x,
      };
      setPosition(position);
      setIsVisible(true);
    }
  }, [tooltipProps.x, tooltipProps.y, tooltipProps.barWidth]);

  // Watch for size changes
  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      updatePosition();
    });

    // Obeserve the size of the container element.
    // Because the content under container have dynamic width by css,
    // And the change of width of tooltip cannot trigger the update of ref.current.
    // So we need to observe the size of the container element to adjust the position of the tooltip.
    resizeObserver.observe(containerRef.current);
    return () => {
      resizeObserver.disconnect();
    };
  }, [updatePosition]);

  return createPortal(
    <SingleChartDataTooltipUI.PositionedContainer
      ref={containerRef}
      style={{
        visibility: isVisible ? 'visible' : 'hidden',
        ...position,
        zIndex,
      }}
    >
      <SingleChartDataTooltipUI.TooltipContainer>
        <SingleChartDataTooltipUI.DataFrame $color={tooltipProps.line.color}>
          <TooltipHeader
            query={query}
            tooltipProps={tooltipProps}
            funnelTooltipContext={funnelTooltipContext}
            singleDayLabel={singleDayLabel}
          />
          <TooltipLineLabel
            showMeasureLabel={displayedMeasures.length > 1}
            showGroupName={
              displayedSlices.length > 1 ||
              // when !!comparisonConfig AND NoBreakdown, there is one displayedSlice
              // but we need to show the group name to differentiate between normal and comparison
              !!comparisonInfo?.comparisonConfig
            }
            lineMeta={lineMeta}
            tooltipProps={tooltipProps}
            query={query}
            displayedMeasures={displayedMeasures}
          />
          <TooltipLineValue
            chartData={chartData}
            tooltipProps={tooltipProps}
            measureFormat={measureFormat}
            funnelTooltipContext={funnelTooltipContext}
            dataWithOthers={chartDataWithOthers}
            linesMetaWithOthers={linesMetaWithOthers}
            singleDayLabel={singleDayLabel}
            isFunnelRelativeLabels={isFunnelRelativeLabels}
          />
          {chartData.queryType === QueryType.Expression && !singleDayLabel && (
            <TooltipExpressionDataChangeValue
              linesMeta={linesMetaWithOthers ?? linesMeta}
              data={chartDataWithOthers ?? chartData.data}
              tooltipProps={tooltipProps}
              timeUnit={timeUnit}
            />
          )}
          {tooltipProps.highlight && <div>{tooltipProps.highlight.text}</div>}
        </SingleChartDataTooltipUI.DataFrame>
        {hideMenuItems ? null : isTooltipLocked ? (
          <SingleChartDataTooltipUI.MenuContainer>
            <SingleChartDataTooltipMenuItem
              iconName="users-three"
              label={'Create Cohort'}
              onClick={handleCreateCohortClick}
            />
            <SingleChartDataTooltipMenuItem
              iconName="user-circle"
              label={'Inspect Users'}
              onClick={handleInspectUsersClick}
            />
            {showCreateIncident && (
              <SingleChartDataTooltipMenuItem
                iconName="calendar-dot"
                label={'Create Incident'}
                onClick={handleCreateIncident}
              />
            )}
          </SingleChartDataTooltipUI.MenuContainer>
        ) : (
          <SingleChartDataTooltipUI.ActionHint>
            Click for actions
          </SingleChartDataTooltipUI.ActionHint>
        )}
      </SingleChartDataTooltipUI.TooltipContainer>
    </SingleChartDataTooltipUI.PositionedContainer>,
    document.body,
  );
};

export default SingleChartDataTooltip;
